import { useCallback, useState, useEffect, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { debounce, cloneDeep, isArray, isEmpty, has } from "lodash";
import { eachDeep } from "deepdash-es/standalone";
import { treeActions } from "../../../../redux/slices/treeSlice";
import { TextField } from "@mui/material";
import { t } from "i18next";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { genericRules, validateOnFlow, validateRevertTime, validationTime } from "../../../../utils/validate";
// import { isDuplicateName } from "../RecordPropertyForm"; // Commented out to match SWT behavior
import { getNewRecordName } from "../../../RightContextMenu/ContextActions/NewRecord";

const validationRules = {
  TITLE: genericRules.noSlashRuleObjectJSON,
  LBL_START_POSITION: genericRules.numericRule,
  LBL_NUM_OF_CHARS: genericRules.numericRule,
  LBL_START_OCCUR: genericRules.numericRule,
  LBL_END_OCCUR: genericRules.numericRule
};

export const ForwardSlashError = ({ showError }) => {
  return showError ? <p className="error" style={{ marginTop: '0.2rem' }}>{t('BOX_OBJECT_NAME_INVALID')}</p> : <></>
}

function ObjectProperties() {
  const { t } = useTranslation();
  const parentScreen = useContext(ParentScreenCtx);
  const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
  const selectedNode = rightTree.tree[rightTree.currentTab].selectedNode;
  const translatedData = rightTree.tree[rightTree.currentTab].translatedData;
  const [showError, setShowError] = useState(false);
  const queryParams = useSelector((state) => state.queryParams);
  const templateHeader = rightTree.tree.templateHeader;
  const [error, setError] = useState({});
  const errorRef = useRef(new Object());
  const dispatch = useDispatch();
  const [type, setType] = useState(
    selectedNode.TYPE || ""
  );

  const submitChange = (key, val) => {
    if (
      key !== "TYPE"
    )
      return;

    const updatedData = eachDeep(cloneDeep(translatedData), (objValue) => {
      if (objValue?.key !== selectedNode.key) {
        return;
      }
      objValue[key] = val
      objValue.title = val

      // dispatch(
      //   treeActions.setRightTreeSelectedNode({
      //     screen: parentScreen,
      //     type: rightTree.currentTab,
      //     data: objValue,
      //   })
      // );

      if (isArray(objValue?.children)) {
        return objValue.children;
      }
    });

    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedData,
      })
    );
  };
  const debounceChangeHandler = useCallback(debounce(submitChange, 800), [
    rightTree.currentTab,
    rightTree.tree,
  ]);

  const typeChange = (event) => {
    const previousValue = type;
    const { name, value } = event.target;
    setType(prev => event.target.value);
    const errorState = validateOnFlow(name, value, validationRules);
    const isEmptyObj = isEmpty(errorState);

    setError((previous) => {
      let copyPrevious = JSON.parse(JSON.stringify(previous));
      if (isEmptyObj)
        delete copyPrevious?.[event.target.name]
      else {
        copyPrevious = {
          ...copyPrevious,
          ...errorState
        }
      }
      return copyPrevious;
    })

    if (!isEmpty(errorState)) {
      setTimeout(() => {
        setType((prev) => {
          let val = prev;
          val = val.replaceAll('/', '');
          return val;
        })
      }, validateRevertTime)

      if (errorRef?.current?.[name]) clearTimeout(errorRef?.current[name]);
      const timerId = setTimeout(() => {
        setError((prev) => {
          const copyPrev = JSON.parse(JSON.stringify(prev));
          delete copyPrev[name];
          return copyPrev;
        })
      }, validationTime)
      errorRef.current = { ...errorRef.current, [name]: timerId }
      return;

    }
  }
  const checkType = (event) => {
    const { value, name } = event.target;
    
    // NOTE: Duplicate name validation is commented out to match SWT behavior
    // SWT allows multiple objects with the same name under the same parent
    // const isDuplicate = isDuplicateName(rightTree.tree, value, selectedNode);
    // setError((previous) => {
    //   let copyPrevious = JSON.parse(JSON.stringify(previous));
    //   if (isDuplicate) copyPrevious[name] = `${value} ${t('_ERR_ALREADY_EXISTS')}`;
    //   else {
    //     delete copyPrevious?.[name];
    //   }
    //   return copyPrevious;
    // })

    // if (isDuplicate) {
    //   if (errorRef?.current?.[name]) clearTimeout(errorRef?.current[name]);
    //   const timerId = setTimeout(() => {
    //     setError((prev) => {
    //       const copyPrev = JSON.parse(JSON.stringify(prev));
    //       delete copyPrev[name];
    //       return copyPrev;
    //     })
    //   }, validationTime)
    //   errorRef.current = { ...errorRef.current, [name]: timerId }
    // }

    const finalValue = value || getNewRecordName(rightTree.tree);
    setType(finalValue);
    debounceChangeHandler('TYPE', finalValue);
    event.stopPropagation();
  }

  useEffect(() => {
    const changedNode = rightTree.tree[rightTree.currentTab].selectedNode;
    if (changedNode) {
      setType(changedNode.TYPE || "");
    }
  }, [
    rightTree.tree[rightTree.currentTab].selectedNode.key,
  ]);

  return (
    <>
      <TextField
        variant="outlined"
        size="small"
        autoComplete="off"
        label={t("LBL_NAME")}
        type="text"
        className={"width-100"}
        name={"TITLE"}
        value={type || ''}
        onChange={(e) => {
          typeChange(e)
        }}
        {...(has(error, "TITLE")
          ? {
            helperText: error["TITLE"],
            error: true,
          }
          : {
            helperText: "",
            error: false,
          })}
        onBlur={checkType}
      />
      <ForwardSlashError showError={showError} />
    </>
  )
}

export default ObjectProperties
